import { pickBy } from "lodash-es";
import { environment } from "src/environments";
import { isExistent } from "src/utils";
import {
  ApiUrl,
  OperationName,
  PathParamsOfOperation,
  QueryParamsOfOperation,
} from "src/utils/resource-url-v2/resource-url-v2-types";

export const generateApiURL = <
  Method extends OperationName,
  PathName extends ApiUrl,
  PathParams extends PathParamsOfOperation<PathName, Method>,
  QueryParams extends QueryParamsOfOperation<PathName, Method>,
>({
  prefix = "",
  path,
  pathParams,
  queryParams,
}: {
  prefix?: string;
  path: PathName;
  pathParams?: PathParams;
  queryParams?: QueryParams;
}): string => {
  // Generate dynamic url by replacing its placeholder params
  const replaceURLParams = (
    pathTemplate: ApiUrl,
    pathParams: PathParams | undefined,
  ): string => {
    if (!isExistent(pathParams)) {
      return pathTemplate;
    }
    // The selector (.+?) matches anything between curly braces except newline
    return pathTemplate.replace(
      /{(.+?)}/g,
      (_: unknown, key: keyof PathParams): string =>
        String(pathParams[key] || key),
    );
  };

  const generatedUrl = `${removeTrailingSlash(
    environment.apiUrls.mrv2.href,
  )}${prefix}${replaceURLParams(path, pathParams)}`;

  // Generate query params string
  const cleanedQueryParams =
    queryParams &&
    pickBy(queryParams, (value: string | number) => !isBlank(value));
  const buildURLQuery = (queryParams: object): string =>
    Object.entries(queryParams)
      .map((pair) => pair.map(encodeURIComponent).join("="))
      .join("&");
  const generatedQueryParams =
    cleanedQueryParams && buildURLQuery(cleanedQueryParams);

  return generatedQueryParams
    ? `${generatedUrl}?${generatedQueryParams}`
    : generatedUrl;
};

const isBlank = (value: string | number | undefined): boolean => {
  if (typeof value === "string") {
    return value?.length === 0 || !value?.trim();
  }
  return value === undefined;
};

function removeTrailingSlash(url: string): string {
  return url.replace(/\/$/, "");
}

export const UrlUtils = {
  generateApiURL,
};
